"use client";

/**
 * CountryPhoneInput
 *
 * A phone-number field with a searchable country selector. Replaces the
 * old hardcoded "🇺🇬 +256" prefix so customers from ANY country (Kenya,
 * Nigeria, …) can enter their own number. Defaults to Uganda since that's
 * the primary market, but the user can drop down and pick any country or
 * type the dial code's country name to filter.
 *
 * Design goals:
 *   - No new npm dependencies. Country list + dial codes + validation come
 *     from `libphonenumber-js` (already in the project); country names come
 *     from the built-in `Intl.DisplayNames`; flags are rendered from the
 *     ISO code via Unicode regional-indicator symbols.
 *   - Controlled on two pieces: `country` (ISO) and `value` (the national
 *     number the user typed). The parent submits the combined E.164 string
 *     produced by the exported `toE164(country, value)` helper.
 *
 * Usage:
 *   const [country, setCountry] = useState<CountryCode>("UG");
 *   const [phone, setPhone] = useState("");
 *   <CountryPhoneInput country={country} onCountryChange={setCountry}
 *                      value={phone} onChange={setPhone} />
 *   // on submit:
 *   const e164 = toE164(country, phone); // "+254712345678" | ""
 *   const ok   = isPhoneValid(country, phone);
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
  type CountryCode,
} from "libphonenumber-js";
import { ChevronDown, Search, Check } from "lucide-react";

// ── Helpers (exported so callers can build the submit value) ────────────

/** ISO-3166 alpha-2 → flag emoji via regional-indicator symbols. */
export function isoToFlag(iso: string): string {
  if (!iso || iso.length !== 2) return "🏳️";
  return iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// `Intl.DisplayNames` is available in every modern browser. Guard for SSR
// just in case this module is evaluated server-side.
const regionDisplay =
  typeof Intl !== "undefined" && (Intl as any).DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

export function countryName(iso: string): string {
  try {
    return regionDisplay?.of(iso) || iso;
  } catch {
    return iso;
  }
}

/**
 * Combine the chosen country with the typed national number into an E.164
 * string (e.g. "+254712345678"). Handles trunk-zero prefixes ("0712…") and
 * already-international input ("+254…"). Returns "" when it can't parse —
 * callers treat that as "not yet valid".
 */
export function toE164(country: CountryCode, national: string): string {
  const raw = (national || "").trim();
  if (!raw) return "";
  try {
    const parsed = parsePhoneNumber(raw, country);
    return parsed?.number || "";
  } catch {
    return "";
  }
}

/** True when the typed number is a valid phone number for the chosen country. */
export function isPhoneValid(country: CountryCode, national: string): boolean {
  const raw = (national || "").trim();
  if (!raw) return false;
  try {
    return isValidPhoneNumber(raw, country);
  } catch {
    return false;
  }
}

// Pre-build the country list once. Each entry: ISO, dial code, name, flag.
// Sorted by name so the dropdown reads alphabetically.
interface CountryEntry {
  iso: CountryCode;
  dial: string;
  name: string;
  flag: string;
}

const ALL_COUNTRIES: CountryEntry[] = getCountries()
  .map((iso) => ({
    iso,
    dial: getCountryCallingCode(iso),
    name: countryName(iso),
    flag: isoToFlag(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// ── Component ───────────────────────────────────────────────────────────

interface CountryPhoneInputProps {
  country: CountryCode;
  onCountryChange: (c: CountryCode) => void;
  value: string;
  onChange: (national: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Hex used for the focus ring + selected highlight. Defaults to WhatsApp green. */
  accentColor?: string;
}

export function CountryPhoneInput({
  country,
  onCountryChange,
  value,
  onChange,
  placeholder = "700 123 456",
  disabled = false,
  accentColor = "#075E54",
}: CountryPhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const current = useMemo(
    () => ALL_COUNTRIES.find((c) => c.iso === country) || ALL_COUNTRIES[0],
    [country],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ALL_COUNTRIES;
    // Only match on dial code when the query actually contains digits —
    // otherwise `"".includes("")` would match every country and a text
    // search like "ken" would return the whole list.
    const digits = q.replace(/\D/g, "");
    return ALL_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso.toLowerCase().includes(q) ||
        (digits.length > 0 && c.dial.includes(digits)),
    );
  }, [search]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Focus the search box when the dropdown opens.
  useEffect(() => {
    if (open) {
      // Defer so the element is mounted.
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <div
        className="flex items-stretch rounded-lg overflow-visible transition-colors"
        style={{
          backgroundColor: "#f3f4f6",
          border: `1px solid ${focused || open ? accentColor : "transparent"}`,
        }}
      >
        {/* Country trigger */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="px-2.5 py-2 flex items-center gap-1.5 text-sm rounded-l-lg shrink-0 transition-colors disabled:opacity-50"
          style={{ backgroundColor: "#e5e7eb", color: "#374151" }}
          aria-label="Select country"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-base leading-none">{current.flag}</span>
          <span className="font-medium">+{current.dial}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>

        {/* National number input */}
        <input
          type="tel"
          inputMode="numeric"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.replace(/[^\d\s+-]/g, ""))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm rounded-r-lg outline-none min-w-0 bg-transparent"
          style={{ color: "#111827" }}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute left-0 right-0 bottom-full mb-1 z-[90] rounded-lg shadow-xl border bg-white overflow-hidden"
          style={{ borderColor: "#e5e7eb" }}
        >
          {/* Search */}
          <div
            className="flex items-center gap-2 px-3 py-2 border-b"
            style={{ borderColor: "#f3f4f6" }}
          >
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code…"
              className="flex-1 text-sm outline-none bg-transparent text-gray-900 min-w-0"
            />
          </div>

          {/* List */}
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-gray-400">
                No country matches “{search}”
              </p>
            ) : (
              filtered.map((c) => {
                const selected = c.iso === country;
                return (
                  <button
                    key={c.iso}
                    type="button"
                    onClick={() => {
                      onCountryChange(c.iso);
                      setOpen(false);
                      setSearch("");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    style={selected ? { backgroundColor: "#f0fdf4" } : undefined}
                  >
                    <span className="text-base leading-none shrink-0">
                      {c.flag}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-gray-900">
                      {c.name}
                    </span>
                    <span className="text-gray-500 shrink-0">+{c.dial}</span>
                    {selected && (
                      <Check
                        className="w-4 h-4 shrink-0"
                        style={{ color: accentColor }}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
